import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { camelize } from '../utils/camelize';
import { PaymentTimingValues, DeliveryMethodValues, StatusValues as OrderStatusValues, } from '../constants/orderValues';
import { PaymentMethodValues as InvoicePaymentMethodValues } from '../constants/invoiceValues';
import { StatusValues as SampleOrderStatusValues } from '../constants/sampleOrderValues';
import { StatusValues as DeliveryRequestStatusValues } from '../constants/deliveryRequestValues';
import { StatusValues as SalesContractStatusValues } from '../constants/salesContractValues';
import { VarietyValues, ProcessingValues, } from '../constants/preOrderRequestValues';
import { formatPercentage } from '../utils/format';
import { useLabelForEstimatedPaymentDate } from './useLabel';
import { getOneTimeUpfrontRate, getOneTimeUpfrontRateSelectLabelKey, getInstallmentPaymentsRate, } from '../utils/paymentTimingHandlingRates';
export function useOrderListItem(offerList) {
    var _a = useTranslation(), t = _a.t, i18n = _a.i18n;
    var estimatedPaymentDate = useLabelForEstimatedPaymentDate();
    var paymentTimingItems = React.useMemo(function () {
        return PaymentTimingValues.map(function (value) { return ({
            value: value,
            text: t("params.order.paymentTimings.".concat(camelize(value))),
        }); });
    }, [t]);
    var priceVersion0PaymentTimingItems = React.useMemo(function () {
        return PaymentTimingValues.map(function (value) { return ({
            value: value,
            text: t("params.order.priceVersion0PaymentTimings.".concat(camelize(value))),
        }); });
    }, [t]);
    var paymentTimingFilterItems = React.useMemo(function () {
        return PaymentTimingValues.map(function (value) {
            var labelForVersion0 = t("params.order.priceVersion0PaymentTimings.".concat(camelize(value)));
            var labelForVersion1 = t("params.order.paymentTimings.".concat(camelize(value)));
            var text = labelForVersion0 === labelForVersion1
                ? labelForVersion0
                : "".concat(labelForVersion1, " or ").concat(labelForVersion0);
            return {
                value: value,
                text: text,
            };
        });
    }, [t]);
    var deliveryMethodItems = React.useMemo(function () {
        return DeliveryMethodValues.map(function (value) { return ({
            value: value,
            text: t("params.order.deliveryMethods.".concat(camelize(value))),
        }); });
    }, [t]);
    var statusItems = React.useMemo(function () {
        return OrderStatusValues.map(function (value) { return ({
            value: value,
            text: t("params.order.statuses.".concat(camelize(value))),
        }); });
    }, [t]);
    var handlingRateText = React.useCallback(function (rate) {
        return t("pages.roasterOrder.".concat(rate < 0 ? 'paymentTimingNegativeRate' : 'paymentTimingPositiveRate'), {
            percentage: formatPercentage(Math.abs(rate).toString()),
            positive: rate !== 0 ? '+' : '',
        });
    }, [t]);
    var paymentTimingItemsWithHandlingRates = React.useCallback(function (itemType, paymentTimingHandlingRates) {
        var oneTimeUpfrontRate = Math.abs(Number(getOneTimeUpfrontRate(paymentTimingHandlingRates, itemType === 'TypicaStockItem'))).toString();
        var oneTimeUpfrontRateSelectLabelKey = getOneTimeUpfrontRateSelectLabelKey(paymentTimingHandlingRates);
        var installmentPaymentsRate = function (paymentTiming) {
            return (parseFloat(getInstallmentPaymentsRate(itemType === 'TypicaStockItem', paymentTiming, paymentTimingHandlingRates)) || 0);
        };
        var afterEachDeliveryRate = installmentPaymentsRate('after_each_delivery_rate');
        var oneTimeAfterFirstDeliveryRate = installmentPaymentsRate('one_time_after_first_delivery_rate');
        var oneTimeAfterImportedRate = installmentPaymentsRate('one_time_after_imported_rate');
        var oneTimeSpecifiedDateRate = installmentPaymentsRate('one_time_specified_date_rate');
        var lumpSumPaymentAfterImportRate = installmentPaymentsRate('lump_sum_payment_after_import_rate');
        var twoTimesAfterImportedRate = installmentPaymentsRate('two_times_after_imported_rate');
        var threeTimesAfterImportedRate = installmentPaymentsRate('three_times_after_imported_rate');
        var fourTimesAfterImportedRate = installmentPaymentsRate('four_times_after_imported_rate');
        var fiveTimesAfterImportedRate = installmentPaymentsRate('five_times_after_imported_rate');
        var paymentDueDateText = function (offerList) {
            if (!offerList || !offerList.paymentDueDate) {
                return '';
            }
            var date = new Date(offerList.paymentDueDate).toLocaleString(i18n.language, {
                year: 'numeric',
                month: 'short',
            });
            if (i18n.language.startsWith('ja') ||
                i18n.language.startsWith('ko') ||
                i18n.language.startsWith('zh-TW')) {
                return "(".concat(date, " ").concat(t('pages.roasterOrder.endOfMonth'), ")");
            }
            else {
                return "(".concat(t('pages.roasterOrder.endOfMonth'), " ").concat(date, ")");
            }
        };
        var getAdditionalText = function (item) {
            switch (item.value) {
                case 'one_time_upfront':
                    if (oneTimeUpfrontRateSelectLabelKey &&
                        (offerList === null || offerList === void 0 ? void 0 : offerList.estimatedPaymentDate)) {
                        return (" (".concat(estimatedPaymentDate(offerList), ")") +
                            t(oneTimeUpfrontRateSelectLabelKey, {
                                percentage: formatPercentage(oneTimeUpfrontRate),
                            }));
                    }
                    else if (oneTimeUpfrontRateSelectLabelKey) {
                        return t(oneTimeUpfrontRateSelectLabelKey, {
                            percentage: formatPercentage(oneTimeUpfrontRate),
                        });
                    }
                    else {
                        return '';
                    }
                case 'one_time_specified_date':
                    return (paymentDueDateText(offerList) +
                        handlingRateText(oneTimeSpecifiedDateRate));
                case 'one_time_after_imported':
                    return handlingRateText(oneTimeAfterImportedRate);
                case 'lump_sum_payment_after_import':
                    return handlingRateText(lumpSumPaymentAfterImportRate);
                case 'two_times_after_imported':
                    return handlingRateText(twoTimesAfterImportedRate);
                case 'three_times_after_imported':
                    return handlingRateText(threeTimesAfterImportedRate);
                case 'four_times_after_imported':
                    return handlingRateText(fourTimesAfterImportedRate);
                case 'five_times_after_imported':
                    return handlingRateText(fiveTimesAfterImportedRate);
                case 'one_time_after_first_delivery':
                    return handlingRateText(oneTimeAfterFirstDeliveryRate);
                case 'after_each_delivery':
                    return handlingRateText(afterEachDeliveryRate);
                default:
                    return '';
            }
        };
        return paymentTimingItems.map(function (item) {
            var value = item.value;
            var text = item.text + getAdditionalText(item);
            return { text: text, value: value };
        });
    }, [paymentTimingItems, t, handlingRateText]);
    return {
        paymentTimingItems: paymentTimingItems,
        deliveryMethodItems: deliveryMethodItems,
        statusItems: statusItems,
        paymentTimingFilterItems: paymentTimingFilterItems,
        priceVersion0PaymentTimingItems: priceVersion0PaymentTimingItems,
        paymentTimingItemsWithHandlingRates: paymentTimingItemsWithHandlingRates,
    };
}
export function usePaymentTimingForPriceVersion() {
    var t = useTranslation().t;
    var paymentTiming = React.useCallback(function (order, orderPricePaymentTiming) {
        var _a = order.offerLists, offerLists = _a === void 0 ? [] : _a;
        var paymentTiming = orderPricePaymentTiming || order.paymentTiming;
        var includesPriceVersionZero = offerLists.filter(function (offerList) { return offerList.priceVersion === 0; }).length >
            0;
        return includesPriceVersionZero
            ? t("params.order.priceVersion0PaymentTimings.".concat(camelize(paymentTiming)))
            : t("params.order.paymentTimings.".concat(camelize(paymentTiming)));
    }, [t]);
    return paymentTiming;
}
export function toOptions(items) {
    return items.reduce(function (result, current) {
        result[current.value] = current.text;
        return result;
    }, {});
}
export function useSampleOrderListItem() {
    var t = useTranslation().t;
    var statusItems = React.useMemo(function () {
        return SampleOrderStatusValues.map(function (value) { return ({
            value: value,
            text: t("params.sampleOrder.statuses.".concat(camelize(value))),
        }); });
    }, [t]);
    return {
        statusItems: statusItems,
    };
}
export function useDeliveryRequestListItem() {
    var t = useTranslation().t;
    var statusItems = React.useMemo(function () {
        return DeliveryRequestStatusValues.map(function (value) { return ({
            value: value,
            text: t("params.deliveryRequest.statuses.".concat(camelize(value))),
        }); });
    }, [t]);
    return {
        statusItems: statusItems,
    };
}
export function useInvoiceListItem() {
    var t = useTranslation().t;
    var paymentMethodItems = React.useMemo(function () {
        return InvoicePaymentMethodValues.map(function (value) { return ({
            value: value,
            text: t("params.invoice.paymentMethods.".concat(camelize(value))),
        }); });
    }, [t]);
    return { paymentMethodItems: paymentMethodItems };
}
export function useSalesContractListItem() {
    var t = useTranslation().t;
    var statusItems = React.useMemo(function () {
        return SalesContractStatusValues.map(function (value) { return ({
            value: value,
            text: t("params.salesContract.statuses.".concat(camelize(value))),
        }); });
    }, [t]);
    return { statusItems: statusItems };
}
export function usePreOrderRequestItem() {
    var t = useTranslation().t;
    var processingItems = React.useMemo(function () {
        return ProcessingValues.map(function (processing) { return ({
            value: processing,
            label: t("params.preOrderRequest.processings.".concat(processing)),
        }); });
    }, [t]);
    var varietyItems = React.useMemo(function () {
        return VarietyValues.map(function (variety) { return ({
            value: variety,
            label: t("params.preOrderRequest.varieties.".concat(variety)),
        }); });
    }, [t]);
    return { processingItems: processingItems, varietyItems: varietyItems };
}
